import {
  AlertCircle,
  Award,
  BookOpen,
  Calendar,
  Clock,
  ExternalLink,
  FileText,
  GraduationCap,
  RefreshCw,
  TrendingUp,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Course,
  useStudentCoursesQuery,
  useSyncCoursesMutation,
} from "../api/studentCoursesApi";
import { useAuth } from "../App";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "../components/ui/tooltip";
import { Progress } from "../components/ui/progress";
import { tokenService } from "../services/TokenService";

// Course Card Component
interface CourseCardProps {
  course: Course;
  onViewCourse: (course: Course) => void;
}

function CourseCard({ course, onViewCourse }: CourseCardProps) {
  const progressPercentage =
    course.stats.totalProjects > 0
      ? (course.stats.completedProjects / course.stats.totalProjects) * 100
      : 0;

  const getStatusColor = () => {
    if (
      course.stats.completedProjects === course.stats.totalProjects &&
      course.stats.totalProjects > 0
    ) {
      return "bg-green-500";
    }
    if (course.stats.inProgress > 0) {
      return "bg-blue-500";
    }
    return "bg-gray-300";
  };

  const MAX_TITLE_LENGTH = 25;
  const displayCourseTitle =
    course.title && course.title.length > MAX_TITLE_LENGTH
      ? `${course.title.slice(0, MAX_TITLE_LENGTH)}...`
      : course.title;

  return (
    <Card className="hover:shadow-lg transition-shadow duration-200">
      <CardHeader className="h-20">
        <div className="flex items-start justify-between">
          <div className="flex-1 ">
            <CardTitle className="text-lg font-semibold text-gray-900 line-clamp-2 break-words">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    style={{ overflowWrap: "anywhere", wordBreak: "normal" }}
                    className="block"
                  >
                    {displayCourseTitle}
                  </span>
                </TooltipTrigger>
                <TooltipContent
                  className="max-w-md whitespace-normal break-words"
                  style={{ overflowWrap: "anywhere", wordBreak: "normal" }}
                >
                  {course.title}
                </TooltipContent>
              </Tooltip>
            </CardTitle>
            <CardDescription className="mt-1 text-sm text-gray-600">
              {course.label}
            </CardDescription>
          </div>
          <div className={`w-3 h-3 rounded-full ${getStatusColor()} mt-1`} />
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Moodle Course Completion */}
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600 font-medium">
                Course Completion
              </span>
              <span className="font-semibold text-gray-900">
                {course.stats.completionPercentage ?? 0}%
              </span>
            </div>
            <Progress
              value={course.stats.completionPercentage ?? 0}
              className="h-2.5"
            />
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-blue-500" />
              <div>
                <p className="text-xs text-gray-500">Assignments</p>
                <p className="text-sm font-semibold text-gray-900">
                  {course.stats.totalAssignments}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-green-500" />
              <div>
                <p className="text-xs text-gray-500">Projects</p>
                <p className="text-sm font-semibold text-gray-900">
                  {course.stats.totalProjects}
                </p>
              </div>
            </div>
          </div>

          {/* Status Badges */}
          {course.stats.totalProjects > 0 && (
            <div className="flex gap-2 flex-wrap">
              {course.stats.inProgress > 0 && (
                <Badge variant="default" className="text-xs">
                  {course.stats.inProgress} In Progress
                </Badge>
              )}
              {course.stats.completedProjects > 0 && (
                <Badge
                  variant="secondary"
                  className="text-xs bg-green-100 text-green-800"
                >
                  {course.stats.completedProjects} Completed
                </Badge>
              )}
              {course.stats.notStarted > 0 && (
                <Badge variant="outline" className="text-xs">
                  {course.stats.notStarted} Not Started
                </Badge>
              )}
            </div>
          )}

          {/* View Course Button */}
          <Button
            onClick={() => onViewCourse(course)}
            className="w-full mt-2"
            variant="outline"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            View Course
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Quick Access Project Card Component
interface QuickProjectCardProps {
  project: {
    id: string;
    title: string;
    progress: {
      percentage: number;
      status: string;
    };
    dueDate: string | null;
  };
  onViewProject: (projectId: string) => void;
}

function QuickProjectCard({ project, onViewProject }: QuickProjectCardProps) {
  const getStatusBadge = () => {
    switch (project.progress.status) {
      case "completed":
        return <Badge className="bg-green-100 text-green-800">Completed</Badge>;
      case "in_progress":
        return <Badge className="bg-blue-100 text-blue-800">In Progress</Badge>;
      default:
        return <Badge variant="outline">Not Started</Badge>;
    }
  };
  const MAX_TITLE_LENGTH = 25;
  const displayProjectTitle =
    project.title && project.title.length > MAX_TITLE_LENGTH
      ? `${project.title.slice(0, MAX_TITLE_LENGTH)}...`
      : project.title;

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-3">
          <Tooltip>
            <TooltipTrigger asChild>
              <h4
                className="font-medium text-gray-900 line-clamp-1 flex-1 break-words"
                style={{ overflowWrap: "anywhere", wordBreak: "normal" }}
              >
                {displayProjectTitle}
              </h4>
            </TooltipTrigger>
            <TooltipContent
              className="max-w-md whitespace-normal break-words"
              style={{ overflowWrap: "anywhere", wordBreak: "normal" }}
            >
              {project.title}
            </TooltipContent>
          </Tooltip>
          {getStatusBadge()}
        </div>

        <div className="space-y-2">
          <div className="flex justify-between text-xs text-gray-600">
            <span>Progress</span>
            <span>{Math.round(project.progress.percentage)}%</span>
          </div>
          <Progress value={project.progress.percentage} className="h-1.5" />

          {project.dueDate && (
            <div className="flex items-center gap-1 text-xs text-gray-500 mt-2">
              <Calendar className="h-3 w-3" />
              <span>Due: {new Date(project.dueDate).toLocaleDateString()}</span>
            </div>
          )}

          <Button
            onClick={() => onViewProject(project.id)}
            size="sm"
            variant="ghost"
            className="w-full mt-2"
          >
            Open Project
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function MyCoursesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(tokenService.getToken());

  useEffect(() => {
    const unsubscribe = tokenService.subscribe((newToken) => {
      setToken(newToken);
    });
    return unsubscribe;
  }, []);

  // Fetch student courses
  const {
    data: courses,
    isLoading: isCoursesLoading,
    isError: isCoursesError,
    error: coursesError,
    refetch: refetchCourses,
  } = useStudentCoursesQuery(token);

  // Sync courses mutation
  const syncMutation = useSyncCoursesMutation(token);

  const handleViewCourse = (course: Course) => {
    navigate(`/my-courses/course-details/${course.id}`, {
      state: { course },
    });
  };

  const handleViewProject = (projectId: string) => {
    navigate(`/project-details/${projectId}`, {
      state: { from: "my-courses" },
    });
  };

  const handleSyncCourses = async () => {
    try {
      await syncMutation.mutateAsync();
      toast.success("Courses synced successfully from Moodle");
    } catch (error) {
      toast.error("Failed to sync courses");
    }
  };

  // Get recent projects from all courses
  const recentProjects = React.useMemo(() => {
    if (!courses) return [];

    const allProjects = courses.flatMap((course) =>
      course.projects.map((project) => ({
        ...project,
        courseName: course.title,
      })),
    );

    // Sort by last activity and take top 4
    return allProjects
      .filter((p) => p.progress.status === "in_progress")
      .sort((a, b) => {
        if (!a.progress.lastActivity) return 1;
        if (!b.progress.lastActivity) return -1;
        return (
          new Date(b.progress.lastActivity).getTime() -
          new Date(a.progress.lastActivity).getTime()
        );
      })
      .slice(0, 4);
  }, [courses]);

  // Calculate overall stats
  const overallStats = React.useMemo(() => {
    if (!courses)
      return {
        totalCourses: 0,
        totalProjects: 0,
        completedProjects: 0,
        inProgressProjects: 0,
      };

    return {
      totalCourses: courses.length || 0,
      totalProjects: courses.reduce(
        (sum, c) => sum + (c.stats.totalProjects || 0),
        0,
      ),
      completedProjects: courses.reduce(
        (sum, c) => sum + (c.stats.completedProjects || 0),
        0,
      ),
      inProgressProjects: courses.reduce(
        (sum, c) => sum + (c.stats.inProgress || 0),
        0,
      ),
    };
  }, [courses]);

  return (
    <div className="p-6 space-y-6">
      {/* Welcome Header */}
      <div className="mb-8 mt-4">
        <h1 className="text-3xl font-semibold text-black mb-2">My Courses</h1>
        <p className="text-gray-600">
          View all your courses and their progress
        </p>
      </div>

      {isCoursesLoading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      ) : isCoursesError ? (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <div>
                <h3 className="font-semibold text-red-900">
                  Failed to load courses
                </h3>
                <p className="text-sm text-red-700 mt-1">
                  {coursesError instanceof Error
                    ? coursesError.message
                    : "An error occurred"}
                </p>
                <Button
                  onClick={() => refetchCourses()}
                  variant="outline"
                  size="sm"
                  className="mt-3"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retry
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Courses</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      {overallStats.totalCourses}
                    </p>
                  </div>
                  <GraduationCap className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Projects</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      {overallStats.totalProjects}
                    </p>
                  </div>
                  <BookOpen className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">In Progress</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      {overallStats.inProgressProjects}
                    </p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-orange-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Completed</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      {overallStats.completedProjects}
                    </p>
                  </div>
                  <Award className="h-8 w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-xl font-semibold">
                    <GraduationCap className="h-6 w-6 text-blue-500" />
                    Courses
                  </CardTitle>
                  {/* <CardDescription className="mt-1">
                    All your courses and their progress
                  </CardDescription> */}
                </div>
                <Button
                  onClick={handleSyncCourses}
                  variant="outline"
                  size="sm"
                  disabled={syncMutation.isPending}
                >
                  <RefreshCw
                    className={`h-4 w-4 mr-2 ${syncMutation.isPending ? "animate-spin" : ""}`}
                  />
                  Sync
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {courses && courses.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {courses.map((course) => (
                    <CourseCard
                      key={course.id}
                      course={course}
                      onViewCourse={handleViewCourse}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No courses found
                  </h3>
                  <p className="text-gray-600 mb-4">
                    You are not enrolled in any courses yet
                  </p>
                  <Button onClick={handleSyncCourses} variant="outline">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Sync from Moodle
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {recentProjects.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl font-semibold">
                  <Clock className="h-6 w-6 text-orange-500" />
                  Continue Working
                </CardTitle>
                <CardDescription>Pick up where you left off</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {recentProjects.map((project) => (
                    <QuickProjectCard
                      key={project.id}
                      project={project}
                      onViewProject={handleViewProject}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
